import { TranslateService } from '@ngx-translate/core';

import { BookingDiaryMechanicModel } from './typings/booking-diary.mechanic';
import { BookingDiaryVehicleModel }  from './typings/booking-diary.customer';
import { DropdownOptionModel }       from '../shared/components/dropdown/typings/dropdown-option';

import {
  formatClockLabel,
  SLOT_MINUTES
} from './booking-diary.layout';

// the vehicle dropdown's last choice, which opens the fields for a vehicle not on file yet
export const NEW_VEHICLE_ID : string = 'new';

// the mechanics in name order, as every mechanic dropdown lists them
export function byMechanicName (
  mechanics : Array<BookingDiaryMechanicModel>
) : Array<BookingDiaryMechanicModel> {
  return mechanics
    .slice()
    .sort(( a, b ) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
}

// the mechanics as a dropdown, with the unassigned choice first
export function mechanicOptionsFor ( mechanics : Array<BookingDiaryMechanicModel>, translateService : TranslateService ) : Array<DropdownOptionModel> {
  return [
    {
      label: translateService.instant('JS_SPACE.BOOKINGS.UNASSIGNED'),
      value: null
    },
    ...byMechanicName(mechanics).map(mechanic => ( {
      label: mechanic.name,
      value: mechanic.id
    } ))
  ];
}

// every slot start the shop is open for, as a dropdown
export function timeOptionsBetween ( opensMinutes : number, closesMinutes : number ) : Array<DropdownOptionModel> {
  const options : Array<DropdownOptionModel> = [];

  for ( let minutes = opensMinutes; minutes <= closesMinutes - SLOT_MINUTES; minutes += SLOT_MINUTES ) {
    options.push({
      label: formatClockLabel(minutes),
      value: minutes
    });
  }

  return options;
}

// the typed fields with their edges trimmed, as a form hands them on
export function trimmedFields<T> ( fields : T ) : T {
  const copy : Record<string, string> = {};

  Object.keys(fields).forEach(key => {
    copy[key] = ( fields[key] || '' ).trim();
  });

  return copy as unknown as T;
}

// make, model and plate, whichever the vehicle has
export function vehicleLabel ( vehicle : { make ?: string; model ?: string; plate_number ?: string } ) : string {
  return [ vehicle.make, vehicle.model, vehicle.plate_number ].filter(part => !!part).join(' ');
}

// the customer's vehicles with something to show, after an empty choice that says whether there are any
export function vehicleOptionsFor ( vehicles : Array<BookingDiaryVehicleModel>, translateService : TranslateService ) : Array<DropdownOptionModel> {
  return [
    {
      label: translateService.instant(vehicles.length
        ? 'BOOKING_SPACE.DIARY.NO_VEHICLE'
        : 'BOOKING_SPACE.DIARY.NO_VEHICLES_ON_FILE'),
      value: null
    },
    ...vehicles
      .filter(vehicle => !!vehicleLabel(vehicle))
      .map(vehicle => ( {
        label: vehicleLabel(vehicle),
        value: vehicle.id
      } ))
  ];
}

// the customer's vehicles with the new-vehicle choice last, as both booking modals offer it
export function withNewVehicleOption (
  options          : Array<DropdownOptionModel>,
  translateService : TranslateService
) : Array<DropdownOptionModel> {
  return [
    ...options,
    {
      label: translateService.instant('BOOKING_SPACE.DIARY.ADD_VEHICLE'),
      value: NEW_VEHICLE_ID
    }
  ];
}
